import { filterPillsToAST } from './filterPillsToAST';
import { astToFilterPills } from './astToFilterPills';
import { astToText } from './astToText';

/**
 * Sync UI mode to Advanced mode
 * @param {Array<FilterPill>} filterPills - Filter pills
 * @param {string} quickFilter - Quick filter text
 * @param {Object} schema - Query schema (optional, for user field formatting)
 * @returns {{ast: Object|null, text: string}}
 */
export function syncUIToAdvanced(filterPills, quickFilter, schema = null) {
  // Convert pills → AST (filterPillsToAST)
  const ast = filterPillsToAST(filterPills || [], quickFilter || '');
  
  // Convert AST → text (astToText)
  // If AST is null (no filters), return empty string
  // Pass schema to format user fields correctly
  const text = ast ? astToText(ast, { schema }) : '';
  
  return { ast, text };
}

/**
 * Sync Advanced mode to UI mode
 * @param {Object} ast - Canonical AST
 * @param {Object} schema - Query schema (optional, for converting user IDs to objects)
 * @returns {{filterPills: Array, quickFilter: string, isComplex: boolean, warning: string|null}}
 */
export function syncAdvancedToUI(ast, schema = null) {
  // Convert AST → pills (astToFilterPills)
  // Pass schema to convert user IDs to user objects for display
  const { filterPills, quickFilter, isComplex } = astToFilterPills(ast, schema);
  
  // Return isComplex flag instead of changing mode
  // Mode stays as 'advanced', but Basic button will be disabled if isComplex is true
  return {
    filterPills: isComplex ? [] : filterPills,
    quickFilter: isComplex ? '' : quickFilter,
    isComplex,
    warning: isComplex ? 'This query is too complex for UI mode. Please edit it in Advanced mode.' : null
  };
}

/**
 * Check if mode transition is allowed
 * @param {string} fromMode - Current mode
 * @param {string} toMode - Target mode
 * @param {boolean} isComplex - Whether the current AST is complex
 * @returns {{allowed: boolean, reason: string|null}}
 */
export function canTransitionMode(fromMode, toMode, isComplex = false) {
  // advanced → ui: NOT ALLOWED if AST is complex
  if (fromMode === 'advanced' && toMode === 'ui' && isComplex) {
    return {
      allowed: false,
      reason: 'Cannot switch to Basic mode. The query is too complex to be represented as filter pills.'
    };
  }
  
  // All other transitions: ALLOWED
  return {
    allowed: true,
    reason: null
  };
}

